/**
 * Global Search Enhanced Functionality
 * Provides advanced search features, filters, and interactive elements
 */

class GlobalSearch {
    constructor() {
        this.searchInput = document.getElementById('globalSearch');
        this.searchForm = this.searchInput?.closest('form');
        this.debounceTimer = null;
        this.searchHistory = this.getSearchHistory();
        this.currentFilters = {};
        
        this.init();
    }

    init() {
        if (!this.searchInput) return;

        this.setupEventListeners();
        this.setupTabPersistence();
        this.setupSearchHighlighting();
        this.setupSearchHistory();
        this.setupKeyboardShortcuts();
        this.setupInfiniteScroll();
        this.focusSearchInput();
    }

    setupEventListeners() {
        // Real-time search with debouncing
        this.searchInput.addEventListener('input', (e) => {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => {
                this.handleSearchInput(e.target.value);
            }, 300);
        });

        // Search suggestions
        this.searchInput.addEventListener('focus', () => {
            this.showSearchSuggestions();
        });

        // Hide suggestions when clicking outside
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.search-input-group')) {
                this.hideSearchSuggestions();
            }
        });

        // Form submission
        if (this.searchForm) {
            this.searchForm.addEventListener('submit', (e) => {
                this.handleSearchSubmit(e);
            });
        }

        // Card hover effects
        this.setupCardAnimations();

        // Filter toggles
        this.setupFilterToggles();
    }

    setupTabPersistence() {
        const tabButtons = Array.from(document.querySelectorAll('[data-bs-toggle="pill"]'));
        const getCount = btn => parseInt(btn.dataset.count || '0', 10);

        // Determinar qué pestaña mostrar
        let tabToShow = null;

        if (tabButtons.length) {
            // Primero intentar con la pestaña almacenada en localStorage
            const storedTab = localStorage.getItem('activeSearchTab');
            if (storedTab) {
                const storedBtn = tabButtons.find(btn => btn.id === `${storedTab}-tab`);
                // Usar la pestaña almacenada solo si tiene resultados
                if (storedBtn && getCount(storedBtn) > 0) {
                    tabToShow = storedTab;
                }
            }

            // Si no hay pestaña almacenada válida, buscar la primera con resultados
            if (!tabToShow) {
                const firstWithResults = tabButtons.find(btn => getCount(btn) > 0);
                if (firstWithResults) {
                    tabToShow = firstWithResults.id.replace('-tab', '');
                } else {
                    // Si ninguna tiene resultados, usar la primera (ordenes)
                    tabToShow = 'ordenes';
                }
            }
        }

        // Activar la pestaña seleccionada (Bootstrap 4 con jQuery)
        if (tabToShow && typeof $ !== 'undefined') {
            const tabSelector = `#${tabToShow}-tab`;
            const tabTrigger = $(tabSelector);
            if (tabTrigger.length) {
                tabTrigger.tab('show');
            }
        }

        // Save active tab (jQuery)
        if (typeof $ !== 'undefined') {
            $('[data-bs-toggle="pill"]').on('shown.bs.tab', (e) => {
                const tabId = e.target.id.replace('-tab', '');
                localStorage.setItem('activeSearchTab', tabId);
                this.trackTabChange(tabId);
            });
        }
    }

    setupSearchHighlighting() {
        const searchTerm = this.getSearchTerm();
        if (searchTerm) {
            this.highlightSearchTerms(searchTerm);
        }
    }

    setupSearchHistory() {
        // Add current search to history
        const currentSearch = this.getSearchTerm();
        if (currentSearch && !this.searchHistory.includes(currentSearch)) {
            this.addToSearchHistory(currentSearch);
        }
    }

    setupKeyboardShortcuts() {
        document.addEventListener('keydown', (e) => {
            // Ctrl/Cmd + K to focus search
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                this.focusSearchInput();
            }

            // Escape to clear search
            if (e.key === 'Escape' && document.activeElement === this.searchInput) {
                this.clearSearch();
            }

            // Arrow keys for tab navigation
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                this.handleTabNavigation(e);
            }
        });
    }

    setupInfiniteScroll() {
        // Permitir desactivar comportamiento nativo desde la vista
        if (window.DISABLE_GLOBAL_SEARCH_INFINITE_SCROLL) return;

        // Implement infinite scroll for large result sets
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const activeTab = document.querySelector('.tab-pane.active');
                    const tabOfPagination = entry.target.closest('.tab-pane');

                    // Solo cargar más si la paginación pertenece a la pestaña activa
                    if (!activeTab || !tabOfPagination || tabOfPagination.id !== activeTab.id) {
                        return;
                    }

                    const paginationLink = tabOfPagination.querySelector('.pagination .page-item:last-child a');
                    if (paginationLink && !paginationLink.classList.contains('disabled')) {
                        this.loadMoreResults(paginationLink.href);
                    }
                }
            });
        }, { threshold: 0.1 });

        // Observe pagination containers
        document.querySelectorAll('.tab-pane .pagination').forEach(pagination => {
            observer.observe(pagination.parentElement);
        });
    }

    setupCardAnimations() {
        // Enhanced card hover effects
        document.querySelectorAll('.global-search__card').forEach(card => {
            card.addEventListener('mouseenter', () => {
                card.style.transform = 'translateY(-4px)';
            });

            card.addEventListener('mouseleave', () => {
                card.style.transform = 'translateY(0)';
            });
        });
    }

    setupFilterToggles() {
        // Add filter functionality (would require backend support)
        const filterButtons = document.querySelectorAll('[data-filter]');
        filterButtons.forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                this.toggleFilter(button.dataset.filter, button.dataset.value);
            });
        });
    }

    handleSearchInput(value) {
        if (value.length < 2) return;

        // Show loading state
        this.showLoadingState();

        // Update URL without page reload
        const url = new URL(window.location);
        url.searchParams.set('search', value);
        window.history.replaceState({}, '', url);

        // Trigger search (this would require AJAX implementation)
        // For now, we'll just update the form action
        if (this.searchForm) {
            this.searchForm.action = url.toString();
        }
    }

    handleSearchSubmit(e) {
        const searchValue = this.searchInput.value.trim();
        
        if (!searchValue) {
            e.preventDefault();
            this.showEmptySearchWarning();
            return;
        }

        // Add to search history
        this.addToSearchHistory(searchValue);

        // Show loading state
        this.showLoadingState();
    }

    showSearchSuggestions() {
        if (this.searchHistory.length === 0) return;

        const suggestionsHtml = this.searchHistory
            .slice(0, 5)
            .map(term => `
                <div class="search-suggestion" data-term="${term}">
                    <i class="bi bi-clock-history me-2"></i>
                    ${term}
                </div>
            `)
            .join('');

        const suggestionsContainer = document.createElement('div');
        suggestionsContainer.className = 'search-suggestions';
        suggestionsContainer.innerHTML = suggestionsHtml;

        // Position and show suggestions
        const inputGroup = this.searchInput.closest('.search-input-group');
        inputGroup.appendChild(suggestionsContainer);

        // Handle suggestion clicks
        suggestionsContainer.addEventListener('click', (e) => {
            const suggestion = e.target.closest('.search-suggestion');
            if (suggestion) {
                this.searchInput.value = suggestion.dataset.term;
                this.searchForm.submit();
            }
        });
    }

    hideSearchSuggestions() {
        const suggestions = document.querySelector('.search-suggestions');
        if (suggestions) {
            suggestions.remove();
        }
    }

    highlightSearchTerms(term) {
        const elements = document.querySelectorAll('.meta-value, .title-text');
        const regex = new RegExp(`(${this.escapeRegex(term)})`, 'gi');
        
        elements.forEach(element => {
            if (element.textContent.toLowerCase().includes(term.toLowerCase())) {
                element.innerHTML = element.textContent.replace(regex, '<span class="search-highlight">$1</span>');
            }
        });
    }

    showLoadingState() {
        // Add loading spinner to search button
        const searchBtn = document.querySelector('.search-btn');
        if (searchBtn) {
            const originalContent = searchBtn.innerHTML;
            searchBtn.innerHTML = '<i class="bi bi-arrow-clockwise spin"></i>';
            searchBtn.dataset.originalContent = originalContent;
            searchBtn.disabled = true;
        }

        // Add skeleton loading to content area
        this.showSkeletonLoading();
    }

    hideLoadingState() {
        const searchBtn = document.querySelector('.search-btn');
        if (searchBtn && searchBtn.dataset.originalContent) {
            searchBtn.innerHTML = searchBtn.dataset.originalContent;
            searchBtn.disabled = false;
        }
    }

    showSkeletonLoading() {
        const contentArea = document.querySelector('.global-search__content');
        if (!contentArea) return;

        const skeletonHtml = Array(6).fill().map(() => `
            <div class="col-12 col-lg-6 col-xl-4 mb-3">
                <div class="global-search__card">
                    <div class="card-header">
                        <div class="skeleton skeleton-title"></div>
                    </div>
                    <div class="card-body">
                        <div class="skeleton skeleton-text"></div>
                        <div class="skeleton skeleton-text"></div>
                        <div class="skeleton skeleton-text"></div>
                    </div>
                </div>
            </div>
        `).join('');

        contentArea.innerHTML = `<div class="row">${skeletonHtml}</div>`;
    }

    showEmptySearchWarning() {
        // Add shake animation to search input
        this.searchInput.classList.add('search-error');
        setTimeout(() => {
            this.searchInput.classList.remove('search-error');
        }, 500);

        // Show toast notification
        this.showToast('Por favor ingresa un término de búsqueda', 'warning');
    }

    focusSearchInput() {
        if (this.searchInput && !this.searchInput.value) {
            this.searchInput.focus();
            this.searchInput.select();
        }
    }

    clearSearch() {
        this.searchInput.value = '';
        this.searchInput.focus();
        this.hideSearchSuggestions();
    }

    handleTabNavigation(e) {
        const tabs = document.querySelectorAll('[data-bs-toggle="pill"]');
        const activeTab = document.querySelector('[data-bs-toggle="pill"].active');
        const currentIndex = Array.from(tabs).indexOf(activeTab);
        
        let newIndex;
        if (e.key === 'ArrowLeft') {
            newIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        } else {
            newIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        }

        if (window.bootstrap) {
            new bootstrap.Tab(tabs[newIndex]).show();
        }
    }

    toggleFilter(filterType, value) {
        if (!this.currentFilters[filterType]) {
            this.currentFilters[filterType] = [];
        }

        const index = this.currentFilters[filterType].indexOf(value);
        if (index > -1) {
            this.currentFilters[filterType].splice(index, 1);
        } else {
            this.currentFilters[filterType].push(value);
        }

        this.updateFilters();
    }

    updateFilters() {
        // Update URL with current filters
        const url = new URL(window.location);
        Object.keys(this.currentFilters).forEach(key => {
            if (this.currentFilters[key].length > 0) {
                url.searchParams.set(key, this.currentFilters[key].join(','));
            } else {
                url.searchParams.delete(key);
            }
        });

        window.history.replaceState({}, '', url);
        
        // Trigger search update
        this.applyFilters();
    }

    applyFilters() {
        // Filter visible results client-side or trigger new server request
        const activeTab = document.querySelector('.tab-pane.active');
        if (!activeTab) return;

        const cards = activeTab.querySelectorAll('.global-search__card');
        cards.forEach(card => {
            const shouldShow = this.cardMatchesFilters(card);
            card.style.display = shouldShow ? 'block' : 'none';
        });
    }

    cardMatchesFilters(card) {
        // Implement filter matching logic based on card content
        return true; // Placeholder
    }

    loadMoreResults(url) {
        const activeTab = document.querySelector('.tab-pane.active');
        if (!activeTab) return;

        // Implement infinite scroll loading
        fetch(url)
            .then(response => response.text())
            .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');

                // Limitar a la pestaña activa para evitar mezclar resultados
                const tabFromResponse = doc.querySelector(`#${activeTab.id}`);
                if (!tabFromResponse) return;

                const newCardColumns = Array.from(tabFromResponse.querySelectorAll('.global-search__card'))
                    .map(card => card.closest('[class*="col-"]'))
                    .filter(Boolean);

                const activeRow = activeTab.querySelector('.row');
                if (activeRow && newCardColumns.length > 0) {
                    newCardColumns.forEach(column => {
                        activeRow.appendChild(column);
                    });
                }
            })
            .catch(error => {
                console.error('Error loading more results:', error);
            });
    }

    trackTabChange(tabId) {
        // Analytics tracking
        if (window.gtag) {
            gtag('event', 'search_tab_change', {
                tab_name: tabId,
                search_term: this.getSearchTerm()
            });
        }
    }

    getSearchTerm() {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('search') || '';
    }

    getSearchHistory() {
        const history = localStorage.getItem('searchHistory');
        return history ? JSON.parse(history) : [];
    }

    addToSearchHistory(term) {
        if (!term || this.searchHistory.includes(term)) return;

        this.searchHistory.unshift(term);
        this.searchHistory = this.searchHistory.slice(0, 10); // Keep only last 10
        localStorage.setItem('searchHistory', JSON.stringify(this.searchHistory));
    }

    showToast(message, type = 'info') {
        // Create and show toast notification
        const toast = document.createElement('div');
        toast.className = `toast toast-${type}`;
        toast.innerHTML = `
            <div class="toast-body">
                <i class="bi bi-${type === 'warning' ? 'exclamation-triangle' : 'info-circle'} me-2"></i>
                ${message}
            </div>
        `;

        document.body.appendChild(toast);

        setTimeout(() => {
            toast.classList.add('show');
        }, 100);

        setTimeout(() => {
            toast.remove();
        }, 3000);
    }

    escapeRegex(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    new GlobalSearch();
});

// Add CSS for animations and enhancements
const style = document.createElement('style');
style.textContent = `
    .search-error {
        animation: shake 0.5s ease-in-out;
        border-color: #dc3545 !important;
    }

    @keyframes shake {
        0%, 100% { transform: translateX(0); }
        25% { transform: translateX(-5px); }
        75% { transform: translateX(5px); }
    }

    .spin {
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    .search-suggestions {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #dee2e6;
        border-top: none;
        border-radius: 0 0 8px 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        z-index: 1000;
        max-height: 200px;
        overflow-y: auto;
    }

    .search-suggestion {
        padding: 0.75rem 1rem;
        cursor: pointer;
        transition: background-color 0.2s ease;
        border-bottom: 1px solid #f8f9fa;
    }

    .search-suggestion:hover {
        background-color: #f8f9fa;
    }

    .search-suggestion:last-child {
        border-bottom: none;
    }

    .toast {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 9999;
        padding: 1rem;
        background: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        transform: translateX(100%);
        transition: transform 0.3s ease;
    }

    .toast.show {
        transform: translateX(0);
    }

    .toast-warning {
        border-left: 4px solid #ffc107;
    }

    .toast-info {
        border-left: 4px solid #0dcaf0;
    }
`;
document.head.appendChild(style);
